"use client"

import clsx from "clsx"
import { MouseEvent, ReactNode, Ref } from "react"
import { Tooltip } from "src/components/styled/Tooltip"
import { useEventCallback } from "src/hooks/useEventCallback"
import { A } from "../../../unstyled/A"
import { NpIcon, NpIconName } from "../NpIcon"
import { NpSpinner } from "../NpSpinner"

export type NpButtonChildrenProps =
  | {
      children: ReactNode
      iconNameLeft?: NpIconName
      iconNameRight?: NpIconName
      title?: string
    }
  | {
      children?: undefined
      iconNameLeft: NpIconName
      iconNameRight?: undefined
      title: string
    }
  | {
      children?: undefined
      iconNameLeft?: undefined
      iconNameRight: NpIconName
      title: string
    }

export type NpButtonProps = NpButtonChildrenProps & {
  ref?: Ref<Element>
  id?: string
  type?: "button" | "submit" | "reset"
  disabled?: boolean
  loading?: boolean
  onClick?: (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void
  href?: string
  target?: "_blank"
  color?: "red" | "green" | "blue" | "purple" | "brown" | "gray75" | "gray10"
  variant?: "filled" | "outlined" | "ghost"
  size?: "lg" | "md" | "sm" | "xs"
}

export const NpButton = ({
  ref,
  id,
  children,
  type = "button",
  disabled,
  loading,
  title,
  onClick: emitClick,
  href,
  target,
  color = "green",
  variant = "filled",
  size = "lg",
  iconNameLeft,
  iconNameRight,
}: NpButtonProps) => {
  const Component = href ? A : "button"
  const inert = disabled || loading

  const isIconButton = !children

  const iconClassName = clsx("text-inherit", {
    "size-7": !isIconButton,
    "size-full": isIconButton,
    "p-3": isIconButton && size === "lg",
    "p-2": isIconButton && size === "md",
    "p-1.5": isIconButton && size === "sm",
    "p-1": isIconButton && size === "xs",
  })

  const onClick = useEventCallback((event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (inert) {
      event.preventDefault()
      event.stopPropagation()
      return
    }

    if (emitClick) {
      emitClick(event)
    }
  })

  const button = (
    <Component
      ref={ref as any}
      id={id}
      type={type}
      disabled={inert}
      title={isIconButton ? undefined : title} // icon buttons receive a custom tooltip
      aria-label={title}
      onClick={onClick}
      href={href}
      target={target}
      className={clsx(
        "relative flex max-w-full items-center justify-center overflow-hidden rounded-full text-center leading-tight font-medium no-underline transition hover:transition-none",
        {
          "aspect-square shrink-0": !children,

          "size-13.5": size === "lg" && !children,
          "size-12": size === "md" && !children,
          "size-10": size === "sm" && !children,
          "size-8": size === "xs" && !children,

          "min-h-13.5 px-12 py-2 text-lg": size === "lg" && children,
          "min-h-12 px-9 py-1.5 text-base": size === "md" && children,
          "min-h-10 px-6 py-1 text-sm": size === "sm" && children,
          "min-h-8 px-3 py-0.5 text-xs": size === "xs" && children,

          "pl-9": size === "lg" && children && iconNameLeft,
          "pl-6": size === "md" && children && iconNameLeft,
          "pl-4.5": size === "sm" && children && iconNameLeft,
          "pl-2.25": size === "xs" && children && iconNameLeft,

          "pr-9": size === "lg" && children && iconNameRight,
          "pr-6": size === "md" && children && iconNameRight,
          "pr-4.5": size === "sm" && children && iconNameRight,
          "pr-2.25": size === "xs" && children && iconNameRight,

          "cursor-not-allowed": inert,

          "bg-np-gray10": variant === "filled" && disabled,

          "bg-np-red text-white": color === "red" && variant === "filled" && !disabled,
          "hover:bg-np-red/80 active:bg-np-red": color === "red" && variant === "filled" && !inert,

          "bg-np-green text-white": color === "green" && variant === "filled" && !disabled,
          "hover:bg-np-green/80 active:bg-np-green": color === "green" && variant === "filled" && !inert,

          "bg-np-blue text-white": color === "blue" && variant === "filled" && !disabled,
          "hover:bg-np-blue/80 active:bg-np-blue": color === "blue" && variant === "filled" && !inert,

          "bg-np-purple text-white": color === "purple" && variant === "filled" && !disabled,
          "hover:bg-np-purple/80 active:bg-np-purple": color === "purple" && variant === "filled" && !inert,

          "bg-np-brown text-white": color === "brown" && variant === "filled" && !disabled,
          "hover:bg-np-brown/80 active:bg-np-brown": color === "brown" && variant === "filled" && !inert,

          "bg-np-gray75 text-white": color === "gray75" && variant === "filled" && !disabled,
          "hover:bg-np-gray75/80 active:bg-np-gray75": color === "gray75" && variant === "filled" && !inert,

          "bg-np-gray10 _": color === "gray10" && variant === "filled" && !disabled,
          "hover:bg-np-gray10/80 active:bg-np-gray10": color === "gray10" && variant === "filled" && !inert,

          border: variant === "outlined",

          "opacity-50": variant === "ghost",
          "hover:opacity-100 focus:opacity-100": variant === "ghost" && !inert,

          "text-np-red": color === "red" && variant === "outlined" && !disabled,
          "text-np-green": color === "green" && variant === "outlined" && !disabled,
          "text-np-blue": color === "blue" && variant === "outlined" && !disabled,
          "text-np-purple": color === "purple" && variant === "outlined" && !disabled,
          "text-np-brown": color === "brown" && variant === "outlined" && !disabled,
          "text-np-gray75": color === "gray75" && variant === "outlined" && !disabled,
          "text-np-gray75 _": color === "gray10" && variant === "outlined" && !disabled,

          "hover:text-np-red hover:bg-np-red/10 active:bg-np-red/20":
            color === "red" && (variant === "outlined" || variant === "ghost") && !inert,
          "hover:text-np-green hover:bg-np-green/10 active:bg-np-green/20":
            color === "green" && (variant === "outlined" || variant === "ghost") && !inert,
          "hover:text-np-blue hover:bg-np-blue/10 active:bg-np-blue/20":
            color === "blue" && (variant === "outlined" || variant === "ghost") && !inert,
          "hover:text-np-purple hover:bg-np-purple/10 active:bg-np-purple/20":
            color === "purple" && (variant === "outlined" || variant === "ghost") && !inert,
          "hover:text-np-brown hover:bg-np-brown/10 active:bg-np-brown/20":
            color === "brown" && (variant === "outlined" || variant === "ghost") && !inert,
          "hover:text-np-gray75 hover:bg-np-gray75/10 active:bg-np-gray75/20":
            color === "gray75" && (variant === "outlined" || variant === "ghost") && !inert,
          "hover:text-np-gray25 hover:bg-np-gray25/10 active:bg-np-gray25/20":
            color === "gray10" && (variant === "outlined" || variant === "ghost") && !inert,

          "hover:border-np-red": color === "red" && variant === "outlined" && !inert,
          "hover:border-np-green": color === "green" && variant === "outlined" && !inert,
          "hover:border-np-blue": color === "blue" && variant === "outlined" && !inert,
          "hover:border-np-purple": color === "purple" && variant === "outlined" && !inert,
          "hover:border-np-brown": color === "brown" && variant === "outlined" && !inert,
          "hover:border-np-gray75": color === "gray75" && variant === "outlined" && !inert,
          "hover:border-np-gray25 _": color === "gray10" && variant === "outlined" && !inert,

          "border-np-red": color === "red" && variant === "outlined",
          "border-np-green25": color === "green" && variant === "outlined",
          "border-np-blue25": color === "blue" && variant === "outlined",
          "border-np-purple25": color === "purple" && variant === "outlined",
          "border-np-brown25": color === "brown" && variant === "outlined",
          "border-np-gray25": color === "gray75" && variant === "outlined",
          "border-np-gray10 _": color === "gray10" && variant === "outlined",
        },
      )}
    >
      {loading && (
        <NpSpinner className={clsx("absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2", iconClassName)} />
      )}

      <div
        className={clsx("flex w-full items-center justify-center gap-2", {
          invisible: loading,
          "aspect-square size-full": !children,
        })}
      >
        {iconNameLeft && <NpIcon name={iconNameLeft} className={iconClassName} />}
        {children}
        {iconNameRight && <NpIcon name={iconNameRight} className={iconClassName} />}
      </div>
    </Component>
  )

  if (isIconButton) {
    return <Tooltip text={title || ""}>{button}</Tooltip>
  }

  return button
}
